import React, { useEffect, useRef, useState } from "react";
import {
  DeviceEventEmitter,
  Platform,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import Text from "@/components/shared/AppText";
import { notify } from "@/components/shared/AppToast";
import { brandColor } from "@/components/globalStyle";
import { updateRoom } from "@/components/http/events";
import { t, tc } from "@/localization";
import { forNoun, groupList, roomLabelOf, roomNounOf } from "./rooms";
import useMouseScroll from "./useMouseScroll";
import { ROOMS_CHANGED } from "./useRoomGroup";

const MAX_LENGTH = 40;

// The add chip for each noun, so a translation can agree with it.
const ADD_CHIP = {
  Room: () => t("Add room"),
  Stage: () => t("Add stage"),
  Track: () => t("Add track"),
  Court: () => t("Add court"),
  Field: () => t("Add field"),
  Hall: () => t("Add hall"),
  Camera: () => t("Another camera"),
};

// The selected chip as a name field: Enter, the tick or leaving it saves,
// Escape puts the name back. Guests see the name on the event page.
const RenameChip = ({ member, onDone }) => {
  const [label, setLabel] = useState(roomLabelOf(member));
  const [saving, setSaving] = useState(false);
  const finished = useRef(false);

  const finish = (name) => {
    if (finished.current) return;
    finished.current = true;
    onDone(name);
  };

  const save = async () => {
    if (finished.current || saving) return;
    const cleaned = label.replace(/\s+/g, " ").trim();
    if (!cleaned || cleaned === roomLabelOf(member)) return finish();
    setSaving(true);
    try {
      await updateRoom(member.slug, { room_label: cleaned });
      DeviceEventEmitter.emit(ROOMS_CHANGED);
      DeviceEventEmitter.emit("refresh_event");
      finish(cleaned);
    } catch (err) {
      setSaving(false);
      notify.error(
        t("Couldn't rename"),
        err?.errors?.[0]?.message || err?.message
      );
    }
  };

  return (
    <View
      style={[
        tw`flex-row items-center mr-2 pl-3 pr-1 py-1 rounded-full border`,
        { backgroundColor: "white", borderColor: brandColor },
      ]}
    >
      <TextInput
        value={label}
        onChangeText={setLabel}
        maxLength={MAX_LENGTH}
        autoFocus
        selectTextOnFocus
        editable={!saving}
        returnKeyType="done"
        onSubmitEditing={save}
        onBlur={save}
        onKeyPress={(e) => e.nativeEvent.key === "Escape" && finish()}
        accessibilityLabel={t("New name for {label}", {
          label: roomLabelOf(member),
        })}
        style={[
          tw`text-sm font-semibold text-gray-900 py-0`,
          // Grows with the name, as the chip did.
          { width: Math.min(220, Math.max(72, label.length * 8 + 12)) },
          Platform.OS === "web" && { outlineStyle: "none" },
        ]}
      />
      <Pressable
        onPress={save}
        disabled={saving}
        accessibilityRole="button"
        accessibilityLabel={t("Save name")}
        hitSlop={6}
        style={tw`ml-1 p-1 rounded-full`}
      >
        <Ionicons
          name={saving ? "ellipsis-horizontal" : "checkmark"}
          size={16}
          color={brandColor}
        />
      </Pressable>
    </View>
  );
};

// Chips for every room of a group: the primary first (its link is the main
// one), a red dot when live, a lock when not unlocked yet. `onAdd` shows an
// "Add <noun>" chip at the end. `renamable`: the selected chip has a pencil
// that renames it in place. On web the row also scrolls with the mouse wheel
// and by dragging (useMouseScroll), as it does with a finger.
const RoomSwitcher = ({
  current,
  primary,
  rooms,
  onSelect,
  onAdd,
  renamable = false,
  style,
}) => {
  const scrollRef = useMouseScroll();
  const [renaming, setRenaming] = useState(null);
  // A new name shows until the group reloads with it.
  const [renamed, setRenamed] = useState({});
  useEffect(() => setRenaming(null), [current?.slug]);

  const members = groupList(primary, rooms);
  if (members.length < 2 && !onAdd) {
    return null;
  }
  const addChip = forNoun(ADD_CHIP, roomNounOf(primary || current));
  const labelOf = (member) => {
    const fresh = roomLabelOf(member);
    const pending = renamed[member.slug];
    return pending && pending !== fresh ? pending : fresh;
  };

  return (
    <ScrollView
      ref={scrollRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      style={style}
      contentContainerStyle={tw`py-1 pr-2 items-center`}
    >
      {members.map((member) => {
        const selected = member.slug === current?.slug;
        const isPrimary = !member.room_of;
        if (selected && renaming === member.slug) {
          return (
            <RenameChip
              key={member.slug}
              member={{ ...member, room_label: labelOf(member) }}
              onDone={(name) => {
                if (name)
                  setRenamed((all) => ({ ...all, [member.slug]: name }));
                setRenaming(null);
              }}
            />
          );
        }
        const label = labelOf(member);
        const chip = [
          tw`flex-row items-center mr-2 px-3 py-1.5 rounded-full border`,
          selected
            ? { backgroundColor: brandColor, borderColor: brandColor }
            : tw`bg-white border-gray-300`,
          selected && renamable && tw`pr-1.5`,
        ];
        const content = (
          <>
            {member.event_status === "live" && (
              <View
                style={[
                  tw`w-2 h-2 rounded-full mr-1.5`,
                  { backgroundColor: selected ? "white" : "#ef4444" },
                ]}
              />
            )}
            <Text
              style={[
                tw`text-sm font-semibold`,
                selected ? tw`text-white` : tw`text-gray-800`,
              ]}
            >
              {label}
            </Text>
            {isPrimary && (
              <Text
                style={[
                  tw`text-xs ml-1`,
                  selected ? tw`text-white` : tw`text-gray-500`,
                ]}
              >
                {`· ${tc("room", "Main")}`}
              </Text>
            )}
            {!member.is_published && (
              <Ionicons
                name="lock-closed"
                size={12}
                color={selected ? "white" : "#6b7280"}
                style={tw`ml-1`}
              />
            )}
          </>
        );
        // The selected chip isn't a button (it's already open), so its
        // pencil can be one.
        if (selected) {
          return (
            <View
              key={member.slug}
              accessibilityState={{ selected: true }}
              style={chip}
            >
              {content}
              {renamable && (
                <Pressable
                  onPress={() => setRenaming(member.slug)}
                  accessibilityRole="button"
                  accessibilityLabel={t("Rename {label}", { label })}
                  hitSlop={6}
                  style={({ hovered, pressed }) => [
                    tw`ml-1.5 p-0.5 rounded-full`,
                    (hovered || pressed) && {
                      backgroundColor: "rgba(255,255,255,0.25)",
                    },
                  ]}
                >
                  <Ionicons name="pencil" size={13} color="white" />
                </Pressable>
              )}
            </View>
          );
        }
        return (
          <Pressable
            key={member.slug}
            accessibilityRole="button"
            accessibilityState={{ selected: false }}
            onPress={() => onSelect && onSelect(member)}
            style={chip}
          >
            {content}
          </Pressable>
        );
      })}
      {!!onAdd && (
        <Pressable
          accessibilityRole="button"
          onPress={onAdd}
          style={tw`flex-row items-center px-3 py-1.5 rounded-full border border-dashed border-gray-400 bg-white`}
        >
          <Ionicons name="add" size={16} color="#374151" />
          <Text style={tw`text-sm text-gray-700 ml-1`}>{addChip}</Text>
        </Pressable>
      )}
    </ScrollView>
  );
};

export default RoomSwitcher;
